// ../../lib/excelImport.js
import * as XLSX from "xlsx";

/** normalize strings for matching */
export const norm = (s) =>
  (s ?? "")
    .toString()
    .normalize("NFKD")
    .replace(/[–—−]/g, "-")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

/** Case-insensitive header canonicalization (Opening Stock & Purchase compatible) */
const headerMap = {
  brand: ["brand", "brand name", "prod brand", "product brand"],
  category: ["category", "prod category", "product category"],
  productName: ["product name", "product", "name", "item"],
  // In your data model, 'country' maps to prod_color_name (e.g., MALAYSIA)
  country: ["country origin", "country", "origin", "made in", "color", "colour", "prod color"],
  floor: ["floor", "prod floor", "store floor"],
  rack: ["rack", "prod rack", "store rack", "bin"],
  quantity: ["quantity", "qty", "qnt", "qnty", "pcs"],
  purchaseRate: [
    "purchase rate",
    "rate",
    "buying rate",
    "buy rate",
    "purchase price",
    "unit cost",
    "cost",
  ],
};

const aliasIndex = Object.entries(headerMap).reduce((acc, [canon, aliases]) => {
  aliases.forEach((a) => (acc[norm(a)] = canon));
  return acc;
}, {});

const canonizeRow = (row) => {
  const out = {};
  Object.keys(row || {}).forEach((k) => {
    const mapped = aliasIndex[norm(k)];
    if (mapped) out[mapped] = row[k];
  });
  return out;
};

export const readExcelRows = async (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: "binary" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { defval: "" });
        resolve(rows);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = reject;
    reader.readAsBinaryString(file);
  });

/** Product matching keys */
const keyFromProd = (p) =>
  [p.prod_cat_name, p.prod_brand_name, p.prod_name?.split(" - ")?.[0] ?? p.prod_name, p.prod_color_name]
    .map(norm)
    .join("|");

const keyFromRow = (r) =>
  [r.category, r.brand, r.productName, r.country].map(norm).join("|");

const buildProductIndex = (products) => {
  const m = new Map();
  (products || []).forEach((p) => {
    const k = keyFromProd(p);
    if (!m.has(k)) m.set(k, p);
  });
  return m;
};

/**
 * Validate rows: product combo (Category+Brand+Product Name+Country),
 * floor+rack (iterate all floors with same name; pick first floor that has the rack),
 * numbers.
 */
export const validateExcelRows = (rawRows, products, floors, racks) => {
  const productIndex = buildProductIndex(products);

  const validated = [];
  const invalidRows = [];
  const preview = [];

  (rawRows || []).forEach((raw, i) => {
    const r = canonizeRow(raw);

    const brand = String(r.brand || "").trim();
    const category = String(r.category || "").trim();
    const productName = String(r.productName || "").trim();
    const country = String(r.country || "").trim();
    const floorName = String(r.floor || "").trim();
    const rackName = String(r.rack || "").trim();
    const quantity = parseFloat(r.quantity || 0);
    const purchaseRate = parseFloat(r.purchaseRate || 0);

    const result = {
      Brand: brand,
      Category: category,
      "Product Name": productName,
      "Country Origin": country,
      Floor: floorName,
      Rack: rackName,
      Quantity: quantity,
      "Purchase Rate": purchaseRate,

      isValid: false,
      validationError: "",
      matchedProduct: null,
      matchedFloor: null,
      matchedRack: null,
      _rowIndex: i,
    };

    // Required fields: product combo
    if (!brand || !category || !productName || !country) {
      result.validationError = "Missing Category/Brand/Product/Country.";
      invalidRows.push(i);
      preview.push(result);
      return;
    }

    // Product match
    const prodKey = keyFromRow({ brand, category, productName, country });
    const product = productIndex.get(prodKey);
    if (!product) {
      result.validationError = `No exact product match for [${category} | ${brand} | ${productName} | ${country}].`;
      invalidRows.push(i);
      preview.push(result);
      return;
    }
    result.matchedProduct = product;

    // --- Floor & Rack resolution (UPDATED) ---
    // 1) Gather ALL floors whose name matches (case-insensitive)
    const floorCandidates = (floors || []).filter(
      (f) => norm(f.prod_floor_name) === norm(floorName)
    );

    if (floorCandidates.length === 0) {
      result.validationError = `Floor "${floorName}" not found.`;
      invalidRows.push(i);
      preview.push(result);
      return;
    }

    // 2) For each matching floor, try to find a rack with the given name under that floor.
    //    Take the FIRST floor+rack combination that matches.
    let chosenFloor = null;
    let chosenRack = null;

    for (const f of floorCandidates) {
      const rackCandidate = (racks || []).find(
        (rk) => rk.prod_floor_id === f.prod_floor_id && norm(rk.prod_rack_name) === norm(rackName)
      );
      if (rackCandidate) {
        chosenFloor = f;
        chosenRack = rackCandidate;
        break;
      }
    }

    if (!chosenRack) {
      // We *did* find floors by name, but none of those floors had the rack by that name.
      result.validationError = `Rack "${rackName}" not found in any floor named "${floorName}".`;
      invalidRows.push(i);
      preview.push(result);
      return;
    }

    result.matchedFloor = chosenFloor;
    result.matchedRack = chosenRack;

    // Numbers
    if (!(quantity > 0)) {
      result.validationError = "Invalid quantity.";
      invalidRows.push(i);
      preview.push(result);
      return;
    }
    if (!(purchaseRate > 0)) {
      result.validationError = "Invalid purchase rate.";
      invalidRows.push(i);
      preview.push(result);
      return;
    }

    result.isValid = true;
    preview.push(result);
    validated.push(result);
  });

  return { validated, invalidRows, preview };
};

/** Convert validated rows to Purchase cart + storage cart items */
export const toPurchaseCartItems = (validatedRows) => {
  const purchaseCart = [];
  const storageCart = [];

  (validatedRows || []).forEach((row) => {
    const p = row.matchedProduct;
    const f = row.matchedFloor;
    const r = row.matchedRack;

    purchaseCart.push({
      prod_id: p.prod_id,
      prod_name: p.prod_name,
      prod_brand: p.prod_brand_name,
      prod_purchase_rate: row["Purchase Rate"],
      prod_sale_rate: p.prod_sale_rate || 0,
      prod_qty: row.Quantity,
      prod_total: row["Purchase Rate"] * row.Quantity,
      prod_floor_id: f?.prod_floor_id ?? 0,
      prod_floor_name: f?.prod_floor_name ?? "",
      prod_rack_id: r?.prod_rack_id ?? 0,
      prod_rack_name: r?.prod_rack_name ?? "",
    });

    storageCart.push({
      prod_id: p.prod_id,
      prod_qty: row.Quantity,
      prod_floor_id: f?.prod_floor_id ?? 0,
      prod_rack_id: r?.prod_rack_id ?? 0,
    });
  });

  return { purchaseCart, storageCart };
};
